import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, CardActions, Button, Typography,
  TextField, Grid, InputLabel, MenuItem, FormControl,
  Select, FormControlLabel, Collapse, CircularProgress,
  Divider, Paper, Avatar, Container, Alert, Snackbar,
  Chip, Stack, IconButton, Tooltip, Checkbox, ListItemText
} from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import apiClient from "@/api/client";

const MemberView = () => {
  const theme = useTheme();
  const [role, setRole] = useState('');
  const [errors, setErrors] = useState({});
  const [showCoordinatorPermissions, setShowCoordinatorPermissions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [availableRoles, setAvailableRoles] = useState([]);
  
  const [formData, setFormData] = useState({
    fullName: '',
    nic: '',
    email: '',
    gender: '',
    role: '',
    coordinator_roles: [] // Array of role names instead of individual booleans
  });

  // Coordinator roles list for UI display
  const coordinatorRolesList = [
    { name: "events_coordinator", label: "Events Coordinator", color: "#1976d2" },
    { name: "community_service_coordinator", label: "Community Service Coordinator", color: "#2e7d32" },
    { name: "lunch_dana_coordinator", label: "Lunch Dana Coordinator", color: "#ed6c02" },
    { name: "breakfast_dana_coordinator", label: "Breakfast Dana Coordinator", color: "#f57c00" },
    { name: "meditate_with_us_coordinator", label: "Meditate With Us Coordinator", color: "#9c27b0" },
    { name: "dhamma_talks_coordinator", label: "Dhamma Talks Coordinator", color: "#d32f2f" },
    { name: "arama_poojawa_coordinator", label: "Arama Poojawa Coordinator", color: "#0288d1" },
    { name: "build_up_hermitage_coordinator", label: "Build Up Hermitage Coordinator", color: "#7b1fa2" },
    { name: "donation_coordinator", label: "Donation Coordinator", color: "#c2185b" },
  ];

  // Fetch available roles from API
  useEffect(() => {
    const fetchAvailableRoles = async () => {
      try {
        const response = await apiClient.get('/users/roles');
        const data = response.data;
        if (data.roles) {
          setAvailableRoles(data.roles);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchAvailableRoles();
  }, []);

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    
    if (name === 'coordinator_roles') {
      // Handle multi-select for roles
      setFormData({
        ...formData,
        [name]: value
      });
    } else {
      // Handle regular fields
      setFormData({
        ...formData,
        [name]: value
      });
    }
    setErrors({ ...errors, [name]: "" });
  };

  const handleRoleChange = (event) => {
    const selectedRole = event.target.value;
    setRole(selectedRole);
    setFormData({
      ...formData,
      role: selectedRole,
      // Clear coordinator roles if switching to User
      coordinator_roles: selectedRole === 'Coordinator' ? formData.coordinator_roles : []
    });
    setShowCoordinatorPermissions(selectedRole === 'Coordinator');
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Prepare payload for the API
      const payload = {
        fullName: formData.fullName,
        email: formData.email,
        gender: formData.gender,
        nic: formData.nic,
        role: formData.role,
        coordinator_roles: formData.role === 'Coordinator' ? formData.coordinator_roles : []
      };

      const response = await apiClient.post('/create-user', payload);
      const result = response.data;

      // If successful (Axios won't throw on 2xx)
      setSnackbar({
        open: true,
        message: 'User created successfully!',
        severity: 'success'
      });

      // Reset form after successful submission
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error:', error);
      let errorMessage = 'Failed to create user. Please try again.';
      if (error.response && error.response.data) {
        const result = error.response.data;
        if (result.errors) {
          errorMessage = Object.values(result.errors).flat().join('\n');
        } else if (result.message) {
          errorMessage = result.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    let tempErrors = {};
    let isValid = true;

    if (!formData.fullName.trim()) {
      tempErrors.fullName = "Full Name is required";
      isValid = false;
    }

    if (!formData.nic.trim()) {
      tempErrors.nic = "NIC Number is required";
      isValid = false;
    } else if (!/^\d{9}[vV]?$|^\d{12}$/.test(formData.nic)) {
      tempErrors.nic = "Invalid NIC format (9 digits with v or 12 digits)";
      isValid = false;
    }

    if (!formData.email.trim()) {
      tempErrors.email = "Email is required";
      isValid = false;
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(formData.email)) {
      tempErrors.email = "Invalid email format";
      isValid = false;
    }

    // Validate that at least one role is selected for coordinators
    if (formData.role === 'Coordinator' && formData.coordinator_roles.length === 0) {
      tempErrors.coordinator_roles = "Please select at least one coordinator role";
      isValid = false;
    }

    setErrors(tempErrors);
    return isValid;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      fullName: '',
      nic: '',
      email: '',
      gender: '',
      role: '',
      coordinator_roles: []
    });
    setRole('');
    setShowCoordinatorPermissions(false);
    setErrors({});
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: '#f5f5f5',
      py: 4,
      px: { xs: 2, sm: 3, md: 4 }
    }}>
      <Container maxWidth="lg">
        {/* Header Card */}
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: 'white',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
            <PersonAddIcon />
          </Avatar>
          <Box>
            <Typography variant="h5" fontWeight="600">
              User Registration
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new user account with appropriate permissions
            </Typography>
          </Box>
        </Paper>

        {/* Main Form Card */}
        <Card 
          elevation={0}
          sx={{ 
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden'
          }}
        >
          <CardContent sx={{ p: 4 }}>
            {/* Basic Information Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                Basic Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter the user's personal details
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="fullName"
                    label="Full Name"
                    placeholder="Enter user's full name"
                    variant="outlined"
                    value={formData.fullName}
                    onChange={handleChange}
                    error={!!errors.fullName}
                    helperText={errors.fullName}
                    required
                    size="medium"
                    InputProps={{
                      sx: { borderRadius: 1.5 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="nic"
                    label="NIC Number"
                    placeholder="Enter NIC number"
                    variant="outlined"
                    value={formData.nic}
                    onChange={handleChange}
                    error={!!errors.nic}
                    helperText={errors.nic}
                    required
                    size="medium"
                    InputProps={{
                      sx: { borderRadius: 1.5 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email Address"
                    placeholder="Enter email address"
                    variant="outlined"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    required
                    size="medium"
                    InputProps={{
                      sx: { borderRadius: 1.5 }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={formData.gender}
                      onChange={handleChange}
                      label="Gender"
                      name="gender"
                      sx={{ borderRadius: 1.5 }}
                    >
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="medium">
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={role}
                      onChange={handleRoleChange}
                      label="Role"
                      sx={{ borderRadius: 1.5 }}
                    >
                      <MenuItem value="User">User</MenuItem>
                      <MenuItem value="Coordinator">Coordinator</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Coordinator Permissions Section */}
            <Collapse in={showCoordinatorPermissions}>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="h6" fontWeight="600">
                    Coordinator Roles
                  </Typography>
                  <Tooltip title="Select the roles this coordinator can manage">
                    <IconButton size="small">
                      <InfoIcon fontSize="small" color="action" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Assign specific coordinator roles to this user
                </Typography>

                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 3, 
                    bgcolor: '#fafafa',
                    borderRadius: 2
                  }}
                >
                  <FormControl fullWidth error={!!errors.coordinator_roles}>
                    <InputLabel>Select Coordinator Roles</InputLabel>
                    <Select
                      multiple
                      label="Select Coordinator Roles"
                      name="coordinator_roles"
                      value={formData.coordinator_roles}
                      onChange={handleChange}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((roleName) => {
                            const role = coordinatorRolesList.find(r => r.name === roleName);
                            return (
                              <Chip
                                key={roleName}
                                label={role?.label || roleName.replace(/_/g, ' ')}
                                size="small"
                                sx={{ 
                                  bgcolor: role?.color,
                                  color: 'white',
                                  '& .MuiChip-label': { color: 'white' }
                                }}
                              />
                            );
                          })}
                        </Box>
                      )}
                      sx={{ borderRadius: 1.5 }}
                    >
                      {coordinatorRolesList.map((role) => (
                        <MenuItem key={role.name} value={role.name}>
                          <Checkbox checked={formData.coordinator_roles.indexOf(role.name) > -1} />
                          <ListItemText primary={role.label} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.coordinator_roles && (
                      <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                        {errors.coordinator_roles}
                      </Typography>
                    )}
                  </FormControl>
                </Paper>
              </Box>
            </Collapse>
          </CardContent>

          <Divider />

          {/* Form Actions */}
          <CardActions sx={{ p: 3, bgcolor: '#fafafa', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              startIcon={<CancelIcon />}
              disabled={isLoading}
              sx={{ borderRadius: 2, px: 3 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              sx={{ borderRadius: 2, px: 4 }}
            >
              {isLoading ? 'Creating...' : 'Create User'}
            </Button>
          </CardActions>
        </Card>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default MemberView;